import React, { useEffect, useRef, useState } from 'react';
import { CaixaDeRecorte } from './recorteDeImagem';

type Mode = 'draw' | 'move' | 'resize';
type HandlePos = 'nw' | 'ne' | 'sw' | 'se' | 'e' | 's';

const HANDLE_PX = 12;
// Touch needs a noticeably bigger hit box than a mouse cursor can afford.
const HANDLE_PX_TOUCH = 22;
/** Anything smaller than this is a stray click, not a crop box. */
const MIN_SIZE_PCT = 1.5;

interface Point {
  x: number;
  y: number;
}

interface DragState {
  mode: Mode;
  startPct: Point;
  /** The box as it was when the drag started; move/resize apply the pointer's
   *  total delta to this, never to whatever `onChange` last produced, so
   *  per-frame rounding can't accumulate into drift over a long drag. */
  boxAtStart: CaixaDeRecorte | null;
  pointerId: number;
  /** Which of the six handles started this drag; only set when mode is 'resize'. */
  handle?: HandlePos;
}

interface UseCropBoxOptions {
  box: CaixaDeRecorte | null;
  imageRef: React.RefObject<HTMLImageElement | null>;
  disabled?: boolean;
  onChange: (box: CaixaDeRecorte) => void;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

/** Resize `base` from one of the six handles, given the pointer's % delta since
 *  drag start. Each handle pins the opposite side(s) in place. No aspect-ratio
 *  lock and no rotation — product decisions nobody made (design, Decisão 8). */
function resizeFromHandle(base: CaixaDeRecorte, handle: HandlePos, dx: number, dy: number): CaixaDeRecorte {
  switch (handle) {
    case 'nw': {
      const x = clamp(base.x + dx, 0, base.x + base.w - MIN_SIZE_PCT);
      const y = clamp(base.y + dy, 0, base.y + base.h - MIN_SIZE_PCT);
      return { x, y, w: base.x + base.w - x, h: base.y + base.h - y };
    }
    case 'ne': {
      const w = clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x);
      const y = clamp(base.y + dy, 0, base.y + base.h - MIN_SIZE_PCT);
      return { x: base.x, y, w, h: base.y + base.h - y };
    }
    case 'sw': {
      const x = clamp(base.x + dx, 0, base.x + base.w - MIN_SIZE_PCT);
      const h = clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y);
      return { x, y: base.y, w: base.x + base.w - x, h };
    }
    case 'se': {
      const w = clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x);
      const h = clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y);
      return { x: base.x, y: base.y, w, h };
    }
    case 'e':
      return { ...base, w: clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x) };
    case 's':
      return { ...base, h: clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y) };
  }
}

/**
 * Drag / resize a single crop box over an image, in % of the rendered `<img>`
 * box so the coords survive any display size — the same measurement
 * `useMaskDraw` uses, for the same reason (`offsetWidth` of a wrapper is not
 * the image's own box).
 *
 * Sibling of `OcclusionEditor/useMaskDraw`, which manages a list of masks with
 * selection; this hook only ever holds one box, so there is no selection
 * concept to carry. `box` starts `null` (nothing to crop yet): the first
 * pointer gesture draws it. Once it exists, a gesture starting inside it
 * moves it, one starting on a handle resizes it, and one starting outside it
 * is a no-op — a stray click can never replace or duplicate the one box.
 */
export default function useCropBox({ box, imageRef, disabled = false, onChange }: UseCropBoxOptions) {
  const [draft, setDraft] = useState<CaixaDeRecorte | null>(null);
  const drag = useRef<DragState | null>(null);

  // Window listeners are registered once, so they read live values through this ref
  // instead of closing over a stale render.
  const latest = useRef({ draft, disabled, onChange });
  latest.current = { draft, disabled, onChange };

  const pctFromEvent = (clientX: number, clientY: number): Point | null => {
    const el = imageRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return {
      x: ((clientX - r.left) / r.width) * 100,
      y: ((clientY - r.top) / r.height) * 100,
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    const el = imageRef.current;
    if (!el) return;

    const p = pctFromEvent(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    if (!box) {
      drag.current = { mode: 'draw', startPct: p, boxAtStart: null, pointerId: e.pointerId };
      setDraft({ x: p.x, y: p.y, w: 0, h: 0 });
      return;
    }

    const r = el.getBoundingClientRect();
    // Touch needs the bigger tolerance — a finger can't land as precisely as a cursor.
    const handlePx = e.pointerType === 'touch' ? HANDLE_PX_TOUCH : HANDLE_PX;
    const handleW = (handlePx / r.width) * 100;
    const handleH = (handlePx / r.height) * 100;

    // Handles win over the move zone, picked by nearest (normalized to the
    // tolerance box) rather than a fixed check order, same as useMaskDraw.
    const handlePoints: [HandlePos, Point][] = [
      ['nw', { x: box.x, y: box.y }],
      ['ne', { x: box.x + box.w, y: box.y }],
      ['sw', { x: box.x, y: box.y + box.h }],
      ['se', { x: box.x + box.w, y: box.y + box.h }],
      ['e', { x: box.x + box.w, y: box.y + box.h / 2 }],
      ['s', { x: box.x + box.w / 2, y: box.y + box.h }],
    ];
    let best: { handle: HandlePos; score: number } | null = null;
    for (const [handle, pt] of handlePoints) {
      const ndx = (p.x - pt.x) / handleW;
      const ndy = (p.y - pt.y) / handleH;
      if (Math.abs(ndx) <= 1 && Math.abs(ndy) <= 1) {
        const score = ndx * ndx + ndy * ndy;
        if (!best || score < best.score) best = { handle, score };
      }
    }
    if (best) {
      drag.current = { mode: 'resize', startPct: p, boxAtStart: box, pointerId: e.pointerId, handle: best.handle };
      return;
    }

    if (p.x >= box.x && p.x <= box.x + box.w && p.y >= box.y && p.y <= box.y + box.h) {
      drag.current = { mode: 'move', startPct: p, boxAtStart: box, pointerId: e.pointerId };
      return;
    }

    // Outside the existing box: not a gesture this hook recognizes. The one
    // box is only ever adjusted, never replaced by a stray drag elsewhere.
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId || latest.current.disabled) return;
      const el = imageRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const p = pctFromEvent(e.clientX, e.clientY);
      if (!p) return;

      if (d.mode === 'draw') {
        // the pointer can leave the image mid-drag; keep the rect inside it
        const cx = clamp(p.x, 0, 100);
        const cy = clamp(p.y, 0, 100);
        const x = Math.min(d.startPct.x, cx);
        const y = Math.min(d.startPct.y, cy);
        setDraft({
          x,
          y,
          w: Math.min(Math.abs(cx - d.startPct.x), 100 - x),
          h: Math.min(Math.abs(cy - d.startPct.y), 100 - y),
        });
        return;
      }

      const b = d.boxAtStart;
      if (!b) return;
      const dx = p.x - d.startPct.x;
      const dy = p.y - d.startPct.y;
      const { onChange: change } = latest.current;

      if (d.mode === 'move') {
        const x = clamp(b.x + dx, 0, 100 - b.w);
        const y = clamp(b.y + dy, 0, 100 - b.h);
        change({ ...b, x, y });
      } else {
        change(resizeFromHandle(b, d.handle ?? 'se', dx, dy));
      }
    };

    const endDrag = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      drag.current = null;
      if (d.mode !== 'draw') return;

      const { draft: pending, onChange: change } = latest.current;
      if (pending && pending.w >= MIN_SIZE_PCT && pending.h >= MIN_SIZE_PCT) {
        change(pending);
      }
      setDraft(null);
    };

    // A cancelled pointer (e.g. the OS hands the gesture to something else
    // mid-drag) must end the drag exactly like pointerup, just without
    // committing it — an aborted gesture is not a confirmed one. Without this,
    // `drag.current` would stay set forever: no further pointerup for that
    // pointerId is coming, so the drag would hang until an unrelated pointer
    // happened to reuse the same id.
    const onCancel = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      drag.current = null;
      setDraft(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { draft, handlePointerDown };
}
